<template>
  <section class="admin-dashboard">
    <div class="admin-header">
      <h2>Admin Dashboard</h2>
      <div class="updated" v-if="lastUpdated">
        Last updated {{ lastUpdated }}
      </div>
    </div>

    <div class="admin-filters">
      <div class="filter-field">
        <label>Date range</label>
        <v-date-input
          v-model="dateRange"
          multiple="range"
          clearable
          density="compact"
          variant="outlined"
          :loading="rangeBusy"
          hide-details
        />
      </div>
      <div class="filter-actions">
        <v-btn
          color="primary"
          variant="outlined"
          :loading="backfillBusy"
          :disabled="backfillBusy"
          @click="runBackfill"
        >
          Recalculate stats
        </v-btn>
        <v-btn
          variant="text"
          :disabled="!rangeActive || rangeBusy"
          @click="clearRange"
        >
          Clear
        </v-btn>
      </div>
      <div v-if="rangeActive" class="range-label">
        Showing {{ rangeLabel }}
      </div>
    </div>

    <div class="admin-grid">
      <div class="admin-card">
        <div class="label">Registered users</div>
        <div class="value">
          {{ formatNumber(displayStats?.registeredUsers) }}
        </div>
      </div>
      <div class="admin-card">
        <div class="label">Activated users</div>
        <div class="value">
          {{ formatNumber(displayStats?.activatedUsers) }}
        </div>
      </div>
      <div class="admin-card">
        <div class="label">Activation rate</div>
        <div class="value">{{ activationRate }}</div>
      </div>
      <div class="admin-card">
        <div class="label">Generations</div>
        <div class="value">
          {{ formatNumber(displayStats?.generationsTotal) }}
        </div>
      </div>
      <div class="admin-card">
        <div class="label">Regenerations</div>
        <div class="value">
          {{ formatNumber(displayStats?.regenerationsTotal) }}
        </div>
      </div>
      <div class="admin-card">
        <div class="label">Avg time to first gen</div>
        <div class="value">{{ avgFirstGenTime }}</div>
      </div>
      <div class="admin-card">
        <div class="label">Jobs succeeded</div>
        <div class="value">{{ formatNumber(displayStats?.jobsSucceeded) }}</div>
      </div>
      <div class="admin-card">
        <div class="label">Jobs failed</div>
        <div class="value">{{ formatNumber(displayStats?.jobsFailed) }}</div>
      </div>
      <div class="admin-card">
        <div class="label">Success rate</div>
        <div class="value">{{ successRate }}</div>
      </div>
      <div class="admin-card">
        <div class="label">Uploads</div>
        <div class="value">{{ formatNumber(displayStats?.uploadsTotal) }}</div>
      </div>
      <div class="admin-card">
        <div class="label">Downloads</div>
        <div class="value">{{ formatNumber(displayStats?.downloadsTotal) }}</div>
      </div>
      <div class="admin-card">
        <div class="label">Active subscriptions</div>
        <div class="value">
          {{ formatNumber(displayStats?.subscriptionsActive) }}
        </div>
      </div>
      <div class="admin-card">
        <div class="label">Subscriptions total</div>
        <div class="value">
          {{ formatNumber(displayStats?.subscriptionsTotal) }}
        </div>
      </div>
    </div>

    <div class="admin-section">
      <h3>Generation types</h3>
      <div class="admin-list">
        <div
          v-for="item in generationTypeItems"
          :key="item.label"
          class="admin-list-item"
        >
          <span>{{ item.label }}</span>
          <span>{{ item.value }}</span>
        </div>
      </div>
    </div>

    <div class="admin-section">
      <h3>Resolution usage</h3>
      <div class="admin-list">
        <div
          v-for="item in resolutionItems"
          :key="item.label"
          class="admin-list-item"
        >
          <span>{{ item.label }}</span>
          <span>{{ item.value }}</span>
        </div>
      </div>
    </div>

    <div class="admin-section">
      <h3>Feedback</h3>
      <div v-if="feedbackBusy" class="admin-muted">Loading feedback…</div>
      <div v-else-if="!feedbackItems.length" class="admin-muted">
        No feedback yet.
      </div>
      <v-expansion-panels v-else>
        <v-expansion-panel
          v-for="item in feedbackItems"
          :key="item.id"
          class="feedback-panel"
        >
          <v-expansion-panel-title>
            {{ feedbackTitle(item) }}
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div class="feedback-meta">
              <div><strong>Email:</strong> {{ item.email || "—" }}</div>
              <div><strong>User:</strong> {{ item.userId || "—" }}</div>
              <div><strong>Device:</strong> {{ item.device || "—" }}</div>
              <div><strong>Job ID:</strong> {{ item.jobId || "—" }}</div>
              <div><strong>Context:</strong> {{ item.context || "—" }}</div>
              <div v-if="item.screen">
                <strong>Screen:</strong>
                {{ item.screen.width }} × {{ item.screen.height }}
                <span v-if="item.screen.devicePixelRatio">
                  (DPR {{ item.screen.devicePixelRatio }})
                </span>
              </div>
              <div v-if="item.imageUrl">
                <strong>Image URL:</strong> {{ item.imageUrl }}
              </div>
              <div v-if="item.imagePath">
                <strong>Image path:</strong> {{ item.imagePath }}
              </div>
            </div>
            <div class="feedback-message">
              {{ item.message }}
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      <div v-if="feedbackHasMore" class="feedback-actions">
        <v-btn
          variant="outlined"
          color="primary"
          :loading="feedbackBusy"
          @click="loadFeedbackPage()"
        >
          Load more
        </v-btn>
      </div>
    </div>

    <div class="admin-section">
      <h3>Room types</h3>
      <div class="admin-list">
        <div
          v-for="item in roomTypeItems"
          :key="item.label"
          class="admin-list-item"
        >
          <span>{{ item.label }}</span>
          <span>{{ item.value }}</span>
        </div>
      </div>
    </div>

    <div class="admin-section">
      <h3>Interior styles</h3>
      <div class="admin-list">
        <div
          v-for="item in interiorStyleItems"
          :key="item.label"
          class="admin-list-item"
        >
          <span>{{ item.label }}</span>
          <span>{{ item.value }}</span>
        </div>
      </div>
    </div>

    <div class="admin-section">
      <h3>Outdoor styles</h3>
      <div class="admin-list">
        <div
          v-for="item in outdoorStyleItems"
          :key="item.label"
          class="admin-list-item"
        >
          <span>{{ item.label }}</span>
          <span>{{ item.value }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, shallowRef } from "vue";
import {
  backfillAdminStats,
  fetchAdminStatsRange,
  fetchFeedbackPage,
  type AdminFeedbackItem,
  type AdminStats,
} from "../../lib/admin";
import {
  OUTDOOR_STYLES,
  ROOM_TYPES,
  STYLES,
  RESOLUTION_TIERS,
} from "../../types/presets";
import type { DocumentData, QueryDocumentSnapshot } from "firebase/firestore";

const props = defineProps<{
  stats: AdminStats | null;
}>();

const dateRange = ref<(string | Date)[] | null>(null);
const rangeStats = ref<AdminStats | null>(null);
const rangeBusy = ref(false);
const lastRangeKey = ref("");
const backfillBusy = ref(false);
const feedbackItems = ref<AdminFeedbackItem[]>([]);
const feedbackBusy = ref(false);
const feedbackHasMore = ref(true);
const feedbackCursor = shallowRef<QueryDocumentSnapshot<DocumentData> | null>(null);

const normalizedRange = computed(() => {
  if (!dateRange.value || dateRange.value.length < 2) return null;
  const [first, second] = dateRange.value;
  if (!first || !second) return null;
  const a = toDateKey(first);
  const b = toDateKey(second);
  if (!a || !b) return null;
  return a <= b ? [a, b] : [b, a];
});
const rangeActive = computed(() => !!normalizedRange.value && !!rangeStats.value);
const displayStats = computed(() => rangeStats.value ?? props.stats);
const rangeLabel = computed(() => {
  if (!normalizedRange.value) return "";
  return `${normalizedRange.value[0]} → ${normalizedRange.value[1]}`;
});
const activationRate = computed(() => {
  const registered = displayStats.value?.registeredUsers ?? 0;
  const activated = displayStats.value?.activatedUsers ?? 0;
  if (!registered) return "0%";
  return `${((activated / registered) * 100).toFixed(1)}%`;
});
const avgFirstGenTime = computed(() => {
  const activated = displayStats.value?.activatedUsers ?? 0;
  const total = displayStats.value?.sumTimeToFirstGenMs ?? 0;
  if (!activated || !total) return "—";
  const minutes = total / activated / 60000;
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  const hours = minutes / 60;
  return `${hours.toFixed(1)} h`;
});
const successRate = computed(() => {
  const success = displayStats.value?.jobsSucceeded ?? 0;
  const failed = displayStats.value?.jobsFailed ?? 0;
  const total = success + failed;
  if (!total) return "0%";
  return `${((success / total) * 100).toFixed(1)}%`;
});

const lastUpdated = computed(() => {
  const value = props.stats?.updatedAt as
    | { toDate?: () => Date }
    | undefined;
  if (!value || typeof value.toDate !== "function") return "";
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(
    value.toDate(),
  );
});

const generationTypeItems = computed(() => {
  const types = displayStats.value?.generationTypes ?? {};
  return [
    { label: "Interior", value: formatNumber(types.interior) },
    { label: "Outdoor", value: formatNumber(types.outdoor) },
    { label: "Manual", value: formatNumber(types.manual) },
  ];
});

const interiorStyleItems = computed(() => {
  const styles = displayStats.value?.styleCounts?.interior ?? {};
  return STYLES.map((style) => ({
    label: style,
    value: formatNumber(styles[style]),
  }));
});

const outdoorStyleItems = computed(() => {
  const styles = displayStats.value?.styleCounts?.outdoor ?? {};
  return OUTDOOR_STYLES.map((style) => ({
    label: style,
    value: formatNumber(styles[style]),
  }));
});

const roomTypeItems = computed(() => {
  const rooms = displayStats.value?.roomTypeCounts ?? {};
  return ROOM_TYPES.map((room) => ({
    label: room,
    value: formatNumber(rooms[room]),
  }));
});

const resolutionItems = computed(() => {
  const resolutions = displayStats.value?.resolutionCounts ?? {};
  return RESOLUTION_TIERS.map((resolution) => ({
    label: resolution.toUpperCase(),
    value: formatNumber(resolutions[resolution]),
  }));
});

function clearRange() {
  dateRange.value = null;
  rangeStats.value = null;
  lastRangeKey.value = "";
}

async function runBackfill() {
  backfillBusy.value = true;
  try {
    await backfillAdminStats();
  } finally {
    backfillBusy.value = false;
  }
}

function formatNumber(value?: number) {
  const safe = typeof value === "number" ? value : 0;
  return new Intl.NumberFormat("en-US").format(safe);
}

function toDateKey(value: string | Date) {
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "string" && value.length >= 10) {
    return value.slice(0, 10);
  }
  return "";
}

watch(normalizedRange, async (value) => {
  if (!value) {
    rangeStats.value = null;
    lastRangeKey.value = "";
    return;
  }
  const key = value.join("|");
  if (key === lastRangeKey.value) return;
  rangeBusy.value = true;
  try {
    rangeStats.value = await fetchAdminStatsRange(value[0], value[1]);
    lastRangeKey.value = key;
  } finally {
    rangeBusy.value = false;
  }
});

async function loadFeedbackPage(reset = false) {
  if (feedbackBusy.value) return;
  if (!feedbackHasMore.value && !reset) return;
  feedbackBusy.value = true;
  try {
    if (reset) {
      feedbackItems.value = [];
      feedbackCursor.value = null;
      feedbackHasMore.value = true;
    }
    const { items, lastDoc } = await fetchFeedbackPage(
      10,
      feedbackCursor.value,
    );
    feedbackItems.value = reset
      ? items
      : [...feedbackItems.value, ...items];
    feedbackCursor.value = lastDoc;
    feedbackHasMore.value = items.length === 10;
  } finally {
    feedbackBusy.value = false;
  }
}

onMounted(() => {
  loadFeedbackPage(true);
});

function feedbackTitle(item: AdminFeedbackItem) {
  const date = formatDate(item.createdAt);
  const email = item.email || "Unknown";
  return date ? `${date} - ${email}` : email;
}

function formatDate(value?: unknown) {
  if (!value) return "";
  if (value instanceof Date) {
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(value);
  }
  const maybe = value as { toDate?: () => Date };
  if (maybe && typeof maybe.toDate === "function") {
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(maybe.toDate());
  }
  return "";
}
</script>

<style scoped lang="scss">
.admin-dashboard {
  padding: 2rem 0 4rem;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.admin-header {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.updated {
  color: var(--app-muted-70);
  font-size: 0.9rem;
}

.admin-filters {
  display: grid;
  gap: 0.8rem;
  grid-template-columns: minmax(220px, 1fr) auto;
  align-items: end;
}

.filter-field label {
  display: block;
  font-size: 0.85rem;
  color: var(--app-muted-70);
  margin-bottom: 0.35rem;
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.range-label {
  grid-column: 1 / -1;
  color: var(--app-muted-70);
  font-size: 0.85rem;
}

.admin-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.admin-card {
  padding: 1rem 1.2rem;
  border-radius: 16px;
  background: var(--surface-overlay);
  border: 1px solid var(--accent-border-weak);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.admin-card .label {
  color: var(--app-muted-70);
  font-size: 0.9rem;
}

.admin-card .value {
  font-size: 1.4rem;
  font-weight: 600;
}

.admin-section h3 {
  margin: 0 0 0.6rem;
}

.admin-list {
  display: grid;
  gap: 0.5rem;
}

.admin-list-item {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.6rem 0.8rem;
  border-radius: 12px;
  background: var(--surface-overlay);
  border: 1px solid var(--accent-border-weak);
}

.admin-muted {
  color: var(--app-muted-70);
  font-size: 0.9rem;
}

.feedback-panel {
  border-radius: 12px;
  background: var(--surface-overlay);
  border: 1px solid var(--accent-border-weak);
  overflow: hidden;
}

.feedback-meta {
  display: grid;
  gap: 0.4rem;
  font-size: 0.9rem;
  color: var(--app-muted-70);
  margin-bottom: 0.8rem;
}

.feedback-message {
  white-space: pre-wrap;
  line-height: 1.5;
}

.feedback-actions {
  margin-top: 0.8rem;
  display: flex;
  justify-content: flex-start;
}
</style>
